import { blue, green, deepOrange, teal, red, deepPurple, amber, brown, cyan, indigo, grey, lightBlue, pink, purple, lightGreen, lime, orange, blueGrey, yellow } from "@mui/material/colors";
import { alpha, darken } from "@mui/material/styles";
import CodeUtil from "./code-util";

/*
  COMMON
 */
const CHART_COLORS = [blue, green, deepOrange, teal, red, deepPurple, brown, cyan, indigo, pink, purple, lightBlue, lightGreen, lime, blueGrey, orange];
// 색 수를 넘어 순환할 때 쓰는 밝기 — 100(거의 흰색)·900(거의 검정)은 흰 배경에서 안 보이므로 제외.
const OVERFLOW_BRIGHTNESS = [300, 500, 700, 400, 600, 200, 800];

/*
  차트 시리즈 팔레트 프리셋 — 사용자 선호(Personal Settings 에서 선택, p13n 저장).
  "default" 는 기존 MUI 색상 + 밝기 순환 방식 그대로 유지. 나머지는 CVD·대비 검증된 hex 배열.
  colorSet 이 앱 전역(21개 파일)에서 렌더 중 동기 호출되므로, 활성 팔레트는 모듈 변수로 두고
  앱 초기에 1회 세팅한다(비동기 per-call 조회 불가). 언어(getLocalLang) 로딩과 동일한 캐시 패턴.
*/
// 측정치가 많은 차트(20+)를 위해 각 프리셋을 12색으로 확장(부족분은 colorSet 이 밝기 변주로 계속 생성).
const PRESET_PALETTES = {
  calm: ["#C0704E", "#3E7BB0", "#3FA474", "#8A5AA8", "#C79A3F", "#C25E78", "#2E8E93", "#6E6FB5", "#9C6B4E", "#7E9A3E", "#5D6B99", "#B05C8E"],
  ocean: ["#1565C0", "#EF6C00", "#00897B", "#8E24AA", "#0097A7", "#2E7D32", "#5C6BC0", "#AD1457", "#26A69A", "#7CB342", "#6A1B9A", "#EC407A"],
  vivid: ["#1E88E5", "#E53935", "#00897B", "#FB8C00", "#8E24AA", "#43A047", "#C2185B", "#5E35B1", "#00ACC1", "#F4511E", "#3949AB", "#7CB342"],
  sunset: ["#D9663D", "#7C6BB0", "#E0A32E", "#3F9A6E", "#1C97AE", "#C2557A", "#B0553B", "#8A5AA8", "#A0862E", "#2E8B57", "#6E6FB5", "#C77E1E"],
  jewel: ["#0E8C9E", "#C2557A", "#3F51B5", "#C77E1E", "#2E8B57", "#8E3B70", "#B0347A", "#1A7A8C", "#7E57C2", "#C0704E", "#4527A0", "#2E7D6B"],
  accessible: ["#0072B2", "#D55E00", "#009E73", "#CC79A7", "#56B4E9", "#E69F00", "#6A3D9A"],
};

// hex 를 어둡게(amt>0)/밝게(amt<0) 조정해 **6자리 hex** 로 반환.
//   (원래는 colorByMeasure 가 뒤에 알파 "C5" 를 이어 붙여서 rgb() 문자열이면 깨졌다. 알파는
//    검증 때문에 걷어냈지만, 소비처가 hex 를 문자열로 다루는 곳이 있어 반환 형식은 그대로 둔다.)
const shadeHex = (hex, amt) => {
  const n = parseInt(String(hex).replace("#", ""), 16);
  if (Number.isNaN(n)) return hex;
  const target = amt < 0 ? 255 : 0;   // 음수=밝게(흰 방향), 양수=어둡게(검 방향)
  const p = Math.min(1, Math.abs(amt));
  const ch = (shift) => {
    const c = (n >> shift) & 255;
    return Math.round(c + (target - c) * p).toString(16).padStart(2, "0");
  };
  return `#${ch(16)}${ch(8)}${ch(0)}`;
};

/*
  프리셋 원색은 막대·영역처럼 **면을 통째로 칠하는** 자리에서 화면의 제일 큰 목소리가 된다.
  흰색을 조금 섞어 톤만 낮춘다 — 색상(hue)은 그대로라 시리즈 구분은 유지되고 눈만 편해진다.
  (등급 타일이 쓰는 abcXyzTileBg 와 같은 방식·같은 방향. 값은 거기보다 살짝 약하게.)
  default 팔레트는 MUI [300] 이라 이미 톤이 낮아 그대로 둔다.
  ※ 설정 화면 미리보기 스와치도 같은 값을 써야 한다 — 고른 색과 그려지는 색이 다르면 안 된다.
*/
/*
 * ⚠ 이 값은 취향이 아니라 **검증에 걸린 상한**이다. 흰색을 섞을수록 색이 밝기 밴드(L 0.43~0.77)
 *   위로 밀려나고 채도가 바닥(chroma floor) 아래로 떨어져, 팔레트가 "회색처럼" 읽히기 시작한다.
 *   원래 0.15 였는데 그 값에서는 **원색이 통과하던 ocean/vivid/accessible 이 전부 실패**했다
 *   (ocean: 정상시력 인접 ΔE 16.1 → 13.4, 기준 15). 0.05 까지는 세 프리셋 모두 전 항목 통과,
 *   0.08 부터 다시 깨진다. 톤을 더 낮추고 싶으면 팔레트 검증을 먼저 돌려 볼 것.
 */
const SOFTEN = 0.05;
const soften = (hex, amt = SOFTEN) => shadeHex(hex, -amt);

// 설정 UI 노출용 메타(id · i18n 라벨키 · 미리보기 색). default 는 CHART_COLORS 대표색 6개.
const PALETTE_OPTIONS = [
  { id: "default", labelKey: "chartPaletteDefault", colors: [blue[300], green[300], deepOrange[300], teal[300], red[300], deepPurple[300], brown[300], cyan[300]] },
  { id: "calm", labelKey: "chartPaletteCalm", colors: PRESET_PALETTES.calm.slice(0, 8).map((c) => soften(c)) },
  { id: "ocean", labelKey: "chartPaletteOcean", colors: PRESET_PALETTES.ocean.slice(0, 8).map((c) => soften(c)) },
  { id: "vivid", labelKey: "chartPaletteVivid", colors: PRESET_PALETTES.vivid.slice(0, 8).map((c) => soften(c)) },
  { id: "sunset", labelKey: "chartPaletteSunset", colors: PRESET_PALETTES.sunset.slice(0, 8).map((c) => soften(c)) },
  { id: "jewel", labelKey: "chartPaletteJewel", colors: PRESET_PALETTES.jewel.slice(0, 8).map((c) => soften(c)) },
  { id: "accessible", labelKey: "chartPaletteAccessible", colors: PRESET_PALETTES.accessible.map((c) => soften(c)) },
];
const VALID_PALETTE_IDS = PALETTE_OPTIONS.map((o) => o.id);

// 활성 팔레트 id — 초기값은 localStorage(settings.chartPalette)에서 동기 로드.
let activePaletteId = "default";
try {
  const saved = JSON.parse(localStorage.getItem("settings") || "{}")?.chartPalette;
  if (VALID_PALETTE_IDS.includes(saved)) activePaletteId = saved;
} catch (e) { /* localStorage 파싱 실패 시 default */ }

const setActivePalette = (id) => { activePaletteId = VALID_PALETTE_IDS.includes(id) ? id : "default"; };
const getActivePalette = () => activePaletteId;

const colorSet = (index, initialBrightness = 300) => {
  const preset = PRESET_PALETTES[activePaletteId];
  if (preset) {
    const base = soften(preset[index % preset.length]);
    const cycle = Math.floor(index / preset.length);
    if (cycle === 0) return base;
    // 색 수 초과 시 밝게/어둡게 교대 변주(hex 반환) — 원색과 구분되도록 크게, 검정/흰색은 방지.
    const amt = Math.min(0.42, 0.24 * Math.ceil(cycle / 2));
    return cycle % 2 === 1 ? shadeHex(base, amt) : shadeHex(base, -amt);
  }
  // default: MUI 색상 순환 + 초과 시 밝기 변주(100·900 제외로 항상 가시).
  const colorIndex = index % CHART_COLORS.length;
  const cycle = Math.floor(index / CHART_COLORS.length);
  if (cycle === 0) return CHART_COLORS[index][initialBrightness];
  return CHART_COLORS[colorIndex][OVERFLOW_BRIGHTNESS[cycle % OVERFLOW_BRIGHTNESS.length]];
}

/*
  모듈별 색 — **앱 전체에서 한 모듈은 한 색**이어야 한다(색이 곧 권한 소속이다).
  그래서 순번표를 화면마다 따로 두지 않고 여기 하나만 둔다.

  인덱스는 colorSet 의 핵심 5색(0~4, 서로 뚜렷·조화 보장)에 계획 5모듈을 붙인 것이고,
  AI 는 계획 모듈이 아니라 뒤(6)에 붙였다 — 5 는 Home 의 core 카드가 이미 쓴다.
  실제 hex 는 렌더 시점에 colorSet 으로 뽑으므로 사용자가 고른 팔레트를 그대로 따라간다.

  ⚠️ 모듈을 추가하면 기존 번호를 밀지 말고 뒤에 새 번호를 붙일 것 — 번호를 밀면
     쓰던 화면의 색이 통째로 바뀐다.
  ⚠️ MenuSearch 는 아직 자기 순번(카탈로그 순서 · brightness 500)을 쓴다 — 같은 모듈이
     Home 과 다른 색으로 보인다. 정리 대상이지만 이번 변경 범위 밖이다.
*/
const MODULE_TINT_INDEX = { BF: 0, DP: 1, IP: 2, RP: 3, MP: 4, AI: 6 };

/**
 * 모듈 키 → 모듈명 번역 키.
 *
 * <p>색 순번표 바로 옆에 둔다. 사람이 손으로 쓴 값(태그 등)에서 모듈을 알아내려면
 * 코드(DP)뿐 아니라 <b>화면에 보이는 이름</b>("Demand Plan" · "수요계획")도 받아야 하는데,
 * 그 이름은 언어마다 다르므로 번역 키를 거쳐야 한다. 색과 이름이 따로 놀면 한쪽에만
 * 모듈이 추가되어 색은 있는데 이름이 없는 상태가 된다.
 */
const MODULE_NAME_KEY = { BF: "homeModBf", DP: "homeModDp", IP: "homeModIp", RP: "homeModRp", MP: "homeModMp", AI: "homeAiPrompt" };

/**
 * 회의 안건 태그 중 <b>미리 깔아 둔 것</b>의 색.
 *
 * <p>자유 입력 태그는 색을 받지 않는다. 전부 칠하면 어느 색도 뜻을 갖지 못하고,
 * 색이 붙어 있다는 것 자체가 "이건 제품이 정한 축" 이라는 표시가 된다.
 *
 * <p>계획 모듈(DP·RP·IP·MP)은 여기서 색을 새로 정하지 않고 moduleColor 를 그대로 쓴다 —
 * Home 과 색이 달라지면 "색이 곧 소속" 이라는 규칙이 깨진다. 나머지는 모듈색(0~4)·core(5)·
 * AI(6) 다음 번호를 받는다.
 *
 * <p>목록 정본은 서버(MeetingAgendaService.PREDEFINED_TAGS)다. 거기에 태그를 더하면
 * <b>여기에도 번호를 주어야</b> 색 없는 기본 태그가 생기지 않는다.
 */
const AGENDA_TAG_MODULE = { dp: "DP", rp: "RP", ip: "IP", mp: "MP" };
const AGENDA_TAG_TINT_INDEX = { "s&op": 7, data: 8, common: 9 };

/**
 * 안건 태그 → hex. 미리 깔아 둔 <b>일곱 개만</b> 색을 받고 나머지는 null 이다
 * (tintChipSx 가 null 을 중립색으로 받는다).
 *
 * <p>모듈을 moduleColor 로 통째로 넘기지 않고 네 개를 손으로 적어 둔 이유: 그러면 목록에
 * 없는 "AI" · "BF" 를 자유 입력으로 쳐도 색이 붙는다. 색은 "이건 정해진 축" 이라는 표시인데
 * 아무 태그나 색을 얻으면 그 뜻이 사라진다.
 */
const agendaTagColor = (tag) => {
  const key = String(tag ?? "").trim().toLowerCase().replace(/\s+/g, "");
  const moduleKey = AGENDA_TAG_MODULE[key];
  if (moduleKey) {
    return moduleColor(moduleKey);
  }
  const index = AGENDA_TAG_TINT_INDEX[key];
  return index != null ? colorSet(index) : null;
};

/** 모듈 키(BF/DP/IP/RP/MP/AI) → hex. 모듈이 아니면 null (tintChipSx 가 null 을 중립색으로 받는다). */
const moduleColor = (moduleKey) => {
  const index = MODULE_TINT_INDEX[String(moduleKey ?? "").toUpperCase()];
  return index != null ? colorSet(index) : null;
}

const snackBarMessageColor = {
	dataEmpty : "info",
  exportLimit: "info",
  unSaved: "info",
  noSaveData: "info",
  validation: "info",
  noMatchCondition: "info",
  failAutoRun : "warning",
  warning: "warning",
  saveSuccess : "success",
  confirm: "success",
  saveFail: "error",
  networkError: "error",
  tokenExpired: "error",
  optimisticLock: "error",
  unauthorized: "error",
  inputError: "error"
}

const statusColor = {
  SUCCESS: green[200],
  RUNNING: lightBlue[200],
  FAIL: deepOrange[200],
  STOP: yellow[200],
  IDLE: grey[200],
  WARNING: orange[200],
}

/*
  색이 의미를 갖는 칩(BF 알고리즘, Lifecycle 함수, ABC-XYZ 등급)의 표준 규격.
  원색을 배경에 통째로 칠하고 글자를 흰색으로 두면 칩이 화면에서 제일 큰 목소리가 된다.
  색은 은은하게 깔고 글자·테두리가 색을 갖는다 — 등급은 구분되면서 소리는 낮아진다.
  BfAlgorithmSettings 의 chipSx 가 원본이고, 같은 코드가 여러 곳에 복제돼 있어 여기로 모았다.
*/
const tintChipSx = color => {
  if (!color) {
    return { fontWeight: 700 };
  }
  return {
    bgcolor: alpha(color, 0.12),
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
    fontWeight: 700,
    "& .MuiChip-deleteIcon": { color: alpha(color, 0.6), "&:hover": { color } },
  };
}

// 계획 버전 상태(statusCd) → 대표 hex. tintChipSx 와 함께 상태 칩에 사용 (버전 드롭다운 배지와 동일 계열).
const versionStatusColor = statusCd => {
  switch (statusCd) {
    case "PLANNED":
    case "RUNNING":
    case "SCENARIO":   // 진행 중 작업 버전(DP 시나리오) — planned 와 같은 계열(초록)
      return green[600];
    case "READY":
    case "RELEASING":
      return orange[800];
    case "RELEASED":
      return grey[700];
    case "FAILED":
    case "UNPLANNED":
      return red[600];
    default:
      return grey[600];
  }
}

/*
  위치 유형(CodeUtil.locationType) → 대표 hex. Distribution Network 의 노드 막대·범례가
  같은 색으로 유형을 말하도록 한 곳에 모은다(화면마다 색을 새로 고르지 않게).
  공급 상류(공장·벤더)는 따뜻한 계열, 보관(DC·창고)은 파랑 계열, 수요 말단(점포·고객)은 회색 계열 —
  그래프를 위에서 아래로 훑을 때 색이 흐름의 단계를 따라가게 배치했다.

  colorByMeasure 와 같은 규약: 사용자가 프리셋(default 외)을 고르면 고정색 대신 팔레트를 따른다.
  특히 "accessible" 은 색각 이상 대비용이라, 여기만 고정색으로 남으면 그 사용자에게는
  범례가 다시 구분되지 않는다. 유형 인덱스는 locationType 키 순서라 렌더마다 안 흔들린다.
*/
const LOCATION_TYPE_KEYS = Object.keys(CodeUtil.locationType);

const locationTypeColor = locType => {
  if (activePaletteId !== "default") {
    const index = LOCATION_TYPE_KEYS.indexOf(locType);
    // 알 수 없는 유형(마스터에 없는 코드·빈 값)은 팔레트 색을 낭비하지 않고 회색으로 둔다.
    return index < 0 ? grey[500] : colorSet(index);
  }

  switch (locType) {
    case "PLANT":
      return green[600];
    case "VENDOR":
      return orange[700];
    case "DC":
      return blue[600];
    case "WHSE":
      return cyan[700];
    case "VIRTUAL_DC":
      return blueGrey[400];
    case "STO":
      return deepPurple[300];
    case "CUST":
      return brown[400];
    default:
      return grey[500];
  }
}

/*
  Distribution Network 에서 「따라가는」 품목 경로 색. 경로·거점 테두리·칩·인스펙터 버튼이 같은 색을 쓴다.
  사용자 팔레트를 따른다 — 단, 위치 유형 색(노드 막대·범례)과 선택색(primary 청록)과 색상(hue)이 겹치지
  않는 첫 색을 고른다. 위치 유형이 팔레트 앞 7칸을 쓰므로 그 뒤부터 찾는다.
    예: ocean → 자홍(#AD1457), default → pink, jewel → 보라.
  예전엔 warning.dark(주황)였다 — 팔레트 밖 색이고 ocean 의 DC 주황과 겹쳤으며, "주의"라는 뜻의 색이다.
  ※ accessible 처럼 위치 유형이 팔레트를 다 쓰는 프리셋은 안 겹치는 색이 없어 가장 덜 겹치는 색을 쓴다.
*/
const THEME_PRIMARY = "#048BA8";   // index.js 테마 primary.main — 선택 강조색
const FLOW_MIN_HUE_GAP = 30;
const FLOW_CANDIDATES = 16;

const hexHue = hex => {
  const n = parseInt(String(hex).replace("#", ""), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  if (d === 0) return 0;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return (h * 60 + 360) % 360;
};
const hueGap = (a, b) => {
  const d = Math.abs(a - b);
  return Math.min(d, 360 - d);
};

const flowColorCache = {};
const distNetworkFlowColor = () => {
  if (flowColorCache[activePaletteId]) {
    return flowColorCache[activePaletteId];
  }
  const takenHues = [...LOCATION_TYPE_KEYS.map(locationTypeColor), THEME_PRIMARY].map(hexHue);
  let best = null;
  let bestGap = -1;
  for (let i = LOCATION_TYPE_KEYS.length; i < LOCATION_TYPE_KEYS.length + FLOW_CANDIDATES; i++) {
    // default 팔레트의 [300] 은 선으로 긋기엔 옅다 — 선에 맞게 [600] 을 쓴다(프리셋은 인자를 무시).
    const color = colorSet(i, 600);
    const gap = Math.min(...takenHues.map(h => hueGap(hexHue(color), h)));
    if (gap >= FLOW_MIN_HUE_GAP) {
      best = color;
      break;
    }
    if (gap > bestGap) {
      best = color;
      bestGap = gap;
    }
  }
  flowColorCache[activePaletteId] = best;
  return best;
};

// Job 활성 여부 칩. 원본은 형광에 가까운 #00C851 / #ff4444 볼드 텍스트였다.
const jobActiveColor = {
  active: { text: "#1b7a43", bg: "#e8f5ed" },
  inactive: { text: "#b0453f", bg: "#f7e3e2" },
}

/*
  DATA TRANSFORM 타겟 목록 — 옵션 칩.
  한때 열마다 색을 달리 줬지만(기간=블루, 최신일자=로즈 ...) 화면이 산만해져 걷어냈다.
  옵션만 값이 아니라 "상태"라서 칩으로 남긴다. 나머지 열은 테마의 text.primary/secondary 를 쓴다.

  칩은 배경 위에 글자가 얹히므로 흰 배경이 아니라 칩 배경(bg) 대비로 7:1(WCAG AAA)을 맞춘다 —
  빔프로젝터로 띄우면 대비가 더 깎여 옅은 색은 배경에 묻힌다.
*/
const dataTransformOptionChip = { text: "#0d5145", bg: "#d8efe9" }

/*
  AUDIT TRAIL
  변경 이력의 이벤트 타입 칩 색. tintChipSx 와 함께 쓴다(원색 solid 금지 — 목록에서 칩이 제일 큰 목소리가 된다).
  생성=초록 / 수정=파랑 / 삭제=빨강 은 코드 리뷰 diff 관례를 그대로 따른 것이라 별도 학습이 필요 없다.
*/
const auditOperationColor = {
  INSERT: green[700],
  UPDATE: blue[700],
  DELETE: red[700],
}

/*
  필드 diff 의 이전값/이후값 색. 목록 셀과 상세 팝업이 같은 색을 써야 "빨강=이전, 초록=이후"가 몸에 익는다.
  [800] 을 쓰는 이유: 값 텍스트는 칩이 아니라 본문 위에 얹히므로 흰 배경 대비 7:1(WCAG AAA)이 필요하다.
*/
const auditDiffColor = {
  removed: red[800],
  added: green[800],
}

/*
  ROUND TOGGLE
  원형 토글(월/주/요일/일자) 스와치. createRoundToggleButton 이 인덱싱하는 stop 만 담는다.
    [700] 본색(미선택 글자 / 선택 배경)  [200] 미선택 테두리  [100] hover 배경  [900] hover 글자
  brandTeal 은 테마 primary(#048BA8) 기준. 토/일은 달력 관례라 색이 의미를 갖지만,
  red/blue[700] 원본은 채도가 높아 "주말"이 아니라 "경고"로 읽혀서 톤을 낮춘 값을 쓴다.
 */
const roundToggleColor = {
  brandTeal: { 50: "#e6f4f7", 100: "#cce9ef", 200: "#9fd4de", 700: "#048BA8", 900: "#02576a" },
  weekendSun: { 100: "#f7e3e2", 200: "#e8c0be", 700: "#b0453f", 900: "#8a322d" },
  weekendSat: { 100: "#e3ebf5", 200: "#bcd0e6", 700: "#2f6096", 900: "#224873" },
}
/*
  ABC-XYZ
 */
const { ABCClassConfig } = CodeUtil;
const styleAbcXyzClass = value => {
  const keys = Object.keys(ABCClassConfig);
  if (keys.indexOf(value) < 0) {
    return { backgroundColor: "" };
  }

  return ABCClassConfig[value].style;
}

// ABC-XYZ 등급 배지 = Plannel 틴트 칩 스펙(tintChipSx 철학). ABCClassConfig hue 를 그대로 쓰되
// 원색 solid 대신 톤을 낮춘다: 은은한 배경 + 같은 hue 의 진한 글자·테두리(밝은 ABC 팔레트라 darken 으로 대비 확보).
// 공용 렌더러 components/aggrid/AbcXyzChip.js 가 이걸 소비한다. 값이 등급코드가 아니면 null.
const tintAbcXyzChipSx = value => {
  const base = styleAbcXyzClass(value)?.backgroundColor;
  if (!base) {
    return null;
  }
  return {
    bgcolor: alpha(base, 0.16),
    color: darken(base, 0.42),
    border: `1px solid ${alpha(base, 0.5)}`,
  };
}

// ABC-XYZ 등급(AX~CZ)의 "A안" 솔리드 배경색 — 원 hue 유지 + 흰색 18% 혼합으로 톤만 낮춘 값.
// 원색 solid 는 너무 강해서, 라운드 타일/막대에 쓰기 좋게 채도를 살짝 눌렀다(shadeHex 음수=흰 방향).
// 매트릭스 요약 그리드(AbcXyzAnalysisScenarios)·버전 비교 대시보드 막대(IPComparisonDashboard) 공용.
//   ※ scss/components/AbcXyzAnalysis.scss 의 .high-*/.low-* 하드코딩 값과 같은 톤(함께 맞출 것).
const abcXyzTileBg = value => {
  const base = styleAbcXyzClass(value)?.backgroundColor;
  return base ? shadeHex(base, -0.18) : null;
}

/*
  DP & BF
*/
const algorithmColor = {
  "Croston": green[300],
  "Prophet": deepOrange[300],
  "Random Forest": teal[300],
  "SMA": red[300],
  "GBM": deepPurple[300],
  "Baseline": brown[300],
  "Auto ARIMA": cyan[300],
  "Holt-Winters": indigo[300],
  "ZSMA": pink[300],
  "TimesFM": purple[300],
}
/*
  IP
*/
const MEASURE = { ...CodeUtil.ipRpMeasure, ...CodeUtil.mpMeasure };

/*
 * ⚠ 예전에는 반환 색 뒤에 알파 "C5"(77%)를 붙였다. **뺐다** — 흰 배경 위에서 알파는 흰색 23% 를
 *   섞는 것과 같아서, soften 과 겹쳐 실제로 눈에 닿는 색이 34% 희석되고 있었다.
 *   그 상태로는 soften 을 0.05 로 낮춰도 ocean 이 여전히 실패했고(채도 바닥·정상시력 ΔE 11.6),
 *   알파까지 빼야 전 항목 통과한다. 반투명이 필요한 자리는 area 가 이미 자체 그라데이션을 쓴다.
 */
const colorByMeasure = (id, index) => {
  const brightnessValues = [300, 400, 500, 600, "A200", "A400", 700];
  // 사용자가 프리셋(default 외)을 고르면 measure별 고정색 대신 팔레트(colorSet)를 따른다.
  //   → colorByMeasure 를 쓰는 차트(RP/IP/MP Review 등)도 팔레트가 적용된다. default 는 기존 고정색 유지.
  if (activePaletteId !== "default") {
    return colorSet(index);
  }
  const getColorWithBrightness = (colorPalette, brightness) => colorPalette[brightness];

  let color = getColorWithBrightness(blue, brightnessValues[0]);
  switch (id) {
    case MEASURE.REPLENISH_PLAN_AMT:
    case MEASURE.BACKORDER_QTY:
      color = getColorWithBrightness(green, brightnessValues[0]);
      break;
    case MEASURE.REPLENISH_PLAN_QTY:
      color = getColorWithBrightness(green, brightnessValues[1]);
      break;
    case MEASURE.REPLENISH_PLAN_QTY_ADJ:
      color = getColorWithBrightness(green, brightnessValues[2]);
      break;
    case MEASURE.SALES_QTY:
      color = getColorWithBrightness(blue, brightnessValues[0]);
      break;
    case MEASURE.DEMAND_QTY:
      color = getColorWithBrightness(blue, brightnessValues[1]);
      break;
    case MEASURE.DEMAND_AMT:
    case MEASURE.NET_REQ:
    case "avgSalesMonthlyAmt":
      color = getColorWithBrightness(blue, brightnessValues[2]);
      break;
    case MEASURE.FCST_DEMAND_QTY:
    case "avgSalesMonthly":
      color = getColorWithBrightness(blue, brightnessValues[3]);
      break;
    case MEASURE.FCST_DEMAND_AMT:
    case "totalSales":      
      color = getColorWithBrightness(blue, brightnessValues[4]);
      break;
    case "totalSalesAmt":
    case MEASURE.SALES_AMT:
      color = getColorWithBrightness(blue, brightnessValues[5]);
      break;
    case MEASURE.STOCK_QTY:
    case MEASURE.INV_QTY:
    case "avgStockMonthly":
    case "avgInvMonthly":
      color = getColorWithBrightness(deepOrange, brightnessValues[0]);
      break;
    case MEASURE.STOCK_AMT:
    case MEASURE.INV_AMT:
    case "avgStockMonthlyAmt":
    case "avgInvMonthlyAmt":
      color = getColorWithBrightness(deepOrange, brightnessValues[2]);
      break;
    case MEASURE.GROSS_MARGIN:
      color = getColorWithBrightness(deepOrange, brightnessValues[4]);
      break;
    case MEASURE.SIM_ARRIVAL:
      color = getColorWithBrightness(teal, brightnessValues[0]);
      break;
    case MEASURE.INV_DAYS:
    case "inventoryDays":
    case MEASURE.CAPA_OVER:
      color = getColorWithBrightness(teal, brightnessValues[1]);
      break;
    case MEASURE.SIM_RP_ORDER:
      color = getColorWithBrightness(teal, brightnessValues[2]);
      break;
    case MEASURE.SCHEDULED_RECEIPTS:
      color = getColorWithBrightness(red, brightnessValues[0]);
      break;
    case MEASURE.GROSS_PROFIT:
      color = getColorWithBrightness(red, brightnessValues[1]);
      break;
    case MEASURE.CAPA_UTIL:
      color = getColorWithBrightness(cyan, brightnessValues[0]);
      break;
    case MEASURE.ROP:
    case MEASURE.PROJ_ON_HAND:
      color = getColorWithBrightness(cyan, brightnessValues[1]);
      break;
    case MEASURE.CROSSOVER_RATIO:
      color = getColorWithBrightness(cyan, brightnessValues[2]);
      break;
    case MEASURE.INV_TURNOVER:
    case "inventoryTurnover":
    case MEASURE.CAPA_AVAIL:
    case MEASURE.SIM_SHIPMENT_QTY:
    case MEASURE.PRODUCTION_PLAN:
    case MEASURE.SIM_SUPPLY_QTY:
      color = getColorWithBrightness(brown, brightnessValues[0]);
      break;
    case MEASURE.TARGET_STOCK:
      color = getColorWithBrightness(brown, brightnessValues[1]);
      break;
    case MEASURE.TARGET_STOCK_AMT:
    case MEASURE.SIM_PRODUCTION_PLAN:
      color = getColorWithBrightness(brown, brightnessValues[2]);
      break;
    case MEASURE.PURCHASE_PLAN_QTY:
      color = getColorWithBrightness(orange, brightnessValues[0]);
      break;
    case MEASURE.PURCHASE_PLAN_QTY_ADJ:
    case MEASURE.EOH:
      color = getColorWithBrightness(orange, brightnessValues[1]);
      break;
    case MEASURE.EOH_AMT:
    case MEASURE.SIM_EOH:
      color = getColorWithBrightness(orange, brightnessValues[2]);
      break;
    case MEASURE.PURCHASE_PLAN_AMT:
      color = getColorWithBrightness(orange, brightnessValues[3]);
      break;
    case MEASURE.PROFIT:
    case MEASURE.CAPA_LOAD:
      color = getColorWithBrightness(amber, brightnessValues[3]);
      break;
    case MEASURE.EXP_INV_QTY:
      color = getColorWithBrightness(pink, brightnessValues[0]);
      break;
    case MEASURE.EXP_INV_AMT:
      color = getColorWithBrightness(pink, brightnessValues[2]);
      break;
    case MEASURE.ACTUAL_ARRIVAL_QTY:
      color = getColorWithBrightness(indigo, brightnessValues[0]);
      break;
    case MEASURE.ARRIVAL_QTY:
      color = getColorWithBrightness(indigo, brightnessValues[1]);
      break;
    // case MEASURE.ARRIVAL_QTY_ADJ:
    //   color = getColorWithBrightness(indigo, brightnessValues[3]);
    //   break;
    case MEASURE.ARRIVAL_QTY_CFM:
      color = getColorWithBrightness(indigo, brightnessValues[4]);
      break;
    case MEASURE.ARRIVAL_QTY_GAP:
    case MEASURE.SKU:
      color = getColorWithBrightness(lightGreen, brightnessValues[0]);
      break;
    case MEASURE.ARRIVAL_QTY_PLAN:
      color = getColorWithBrightness(lightGreen, brightnessValues[1]);
      break;
    case MEASURE.ACTIVE_SKU:
      color = getColorWithBrightness(lightGreen, brightnessValues[2]);
      break;
    // case pink 공백.
    case MEASURE.PLAN_ARRIVAL_QTY:
      color = getColorWithBrightness(deepPurple, brightnessValues[0]);
      break;
    case MEASURE.PLAN_ORDER_QTY:
    case MEASURE.GROSS_REQ:
      color = getColorWithBrightness(deepPurple, brightnessValues[1]);
      break;
    case MEASURE.AVG_NO_OF_LOCATION:
    case MEASURE.CAPA_TOTAL:
      color = getColorWithBrightness(purple, brightnessValues[0]);
      break;
    case MEASURE.INTERNAL_DEMAND_QTY:
    case MEASURE.BOH:
      color = getColorWithBrightness(purple, brightnessValues[1]);
      break;
    case MEASURE.SIM_BOH:
      color = getColorWithBrightness(purple, brightnessValues[2]);
      break;
    case MEASURE.INTERNAL_DEMAND_AMT:
    case MEASURE.BOH_AMT:
      color = getColorWithBrightness(purple, brightnessValues[3]);
      break;
    case MEASURE.RP_ORDER:
    case MEASURE.PO_RECEIPTS:
      color = getColorWithBrightness(grey, brightnessValues[0]);
      break;
    // case MEASURE.RP_ORDER_QTY:
    //   color = getColorWithBrightness(grey, brightnessValues[1]);
    //   break;
    // case MEASURE.RP_ORDER_QTY_ADJ:
    case MEASURE.PO_RELEASES:
      color = getColorWithBrightness(grey, brightnessValues[2]);
      break;
    case MEASURE.SHIPMENT_QTY:
      color = getColorWithBrightness(blueGrey, brightnessValues[0]);
      break;
    case MEASURE.SHIPMENT_AMT:
      color = getColorWithBrightness(blueGrey, brightnessValues[2]);
      break;
    case MEASURE.SHIPMENT_QTY_FCST:
      color = getColorWithBrightness(blueGrey, brightnessValues[6]);
      break;
      case MEASURE.SHIPMENT_QTY_REPL:
      color = getColorWithBrightness(blueGrey, brightnessValues[7]);
      break;
    default:
      color = colorSet(index);
      break;
  }

  return color;
}

const ColorUtil = {
  colorByMeasure,
  styleAbcXyzClass,
  tintAbcXyzChipSx,
  abcXyzTileBg,
  snackBarMessageColor,
  colorSet,
  moduleColor,
  agendaTagColor,
  MODULE_NAME_KEY,
  soften,
  statusColor,
  algorithmColor,
  roundToggleColor,
  jobActiveColor,
  dataTransformOptionChip,
  auditOperationColor,
  auditDiffColor,
  tintChipSx,
  versionStatusColor,
  locationTypeColor,
  distNetworkFlowColor,
  // 차트 팔레트 프리셋(사용자 선호)
  PALETTE_OPTIONS,
  setActivePalette,
  getActivePalette
};

export default ColorUtil;